import { Link, useLocation, useRouter } from "@tanstack/react-router";
import { Clock, Compass, LayoutGrid, LogOut, Settings, UserCircle } from "lucide-react";
import { useMemo } from "react";
import { UserAvatar } from "@/components/shared/user-avatar";
import { signOut, useSession } from "@/lib/auth-client";
import { cn } from "@/lib/utils";
import { useSidebar } from "./SidebarContext";

interface NavItem {
  label: string;
  icon: typeof Compass;
  href: string;
  disabled?: boolean;
}

export function Sidebar() {
  const { data: session } = useSession();
  const router = useRouter();
  const location = useLocation();
  const { isMobile, mobileOpen, setMobileOpen } = useSidebar();

  const navItems = useMemo<NavItem[]>(() => {
    const items: NavItem[] = [
      { label: "Dashboard", icon: Compass, href: "/dashboard/waypoint" },
      { label: "Scenarios", icon: LayoutGrid, href: "/dashboard/waypoint/scenarios" },
      { label: "History", icon: Clock, href: "/dashboard/waypoint/history" },
      { label: "Profile", icon: UserCircle, href: "/dashboard/profile" },
    ];

    if (session?.user?.role === "admin") {
      items.push({ label: "Settings", icon: Settings, href: "/dashboard/settings" });
    }

    return items;
  }, [session?.user?.role]);

  async function handleSignOut() {
    await signOut();
    router.navigate({ to: "/login" });
  }

  function handleNavClick() {
    if (isMobile) setMobileOpen(false);
  }

  const sidebarContent = (
    <aside
      style={{ background: "#001847", width: isMobile ? 256 : 232, borderRight: "none" }}
      className="flex flex-col h-full shrink-0"
    >
      {/* Brand */}
      <div
        style={{ borderBottom: "1px solid rgba(255,255,255,0.08)", padding: "0 20px", height: 64 }}
        className="flex items-center"
      >
        <span
          style={{
            fontFamily: "'Montserrat', sans-serif",
            fontWeight: 800,
            fontSize: 20,
            color: "#FAFBFD",
            letterSpacing: "-0.01em",
          }}
        >
          Waypoint
        </span>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-3">
        {navItems.map((item) => {
          const isActive =
            item.href === "/dashboard/waypoint"
              ? location.pathname === "/dashboard/waypoint" ||
                (location.pathname.startsWith("/dashboard/waypoint") &&
                  !location.pathname.startsWith("/dashboard/waypoint/scenarios") &&
                  !location.pathname.startsWith("/dashboard/waypoint/history"))
              : location.pathname.startsWith(item.href);

          return (
            <Link
              key={item.label}
              to={item.href}
              onClick={handleNavClick}
              className={cn(
                "flex items-center gap-3 transition-colors",
                item.disabled && "pointer-events-none",
              )}
              style={{
                padding: "11px 16px",
                margin: "1px 0",
                background: isActive ? "rgba(64,126,193,0.25)" : "transparent",
                boxShadow: isActive ? "inset 2px 0 0 #7FC2EC" : "none",
                color: isActive ? "#FAFBFD" : "rgba(230,238,248,0.6)",
                opacity: item.disabled ? 0.4 : 1,
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}
            >
              <item.icon
                style={{
                  width: 18,
                  height: 18,
                  color: isActive ? "#7FC2EC" : "rgba(230,238,248,0.5)",
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  fontFamily: "'Raleway', sans-serif",
                  fontWeight: 600,
                  fontSize: 14,
                }}
              >
                {item.label}
              </span>
            </Link>
          );
        })}
      </nav>

      {/* User footer */}
      <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", padding: "12px 8px 8px" }}>
        {session?.user && (
          <Link
            to="/dashboard/profile"
            onClick={handleNavClick}
            className="flex items-center gap-3 rounded-md transition-colors"
            style={{
              padding: "8px 12px",
              marginBottom: 4,
              textDecoration: "none",
            }}
          >
            <UserAvatar user={session.user} className="h-8 w-8 shrink-0" />
            <div className="min-w-0">
              <p
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#FAFBFD",
                  margin: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontFamily: "'Raleway', sans-serif",
                }}
              >
                {session.user.name}
              </p>
              <p
                style={{
                  fontSize: 11,
                  color: "rgba(230,238,248,0.5)",
                  margin: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {session.user.email}
              </p>
            </div>
          </Link>
        )}
        <button
          type="button"
          onClick={handleSignOut}
          className="flex w-full items-center gap-3 rounded-md transition-colors"
          style={{
            padding: "8px 12px",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            color: "rgba(230,238,248,0.5)",
          }}
        >
          <LogOut style={{ width: 16, height: 16, flexShrink: 0 }} />
          <span style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 600, fontSize: 13 }}>
            Sign out
          </span>
        </button>
      </div>
    </aside>
  );

  if (isMobile) {
    if (!mobileOpen) return null;
    return (
      <>
        <div className="fixed inset-0 z-40 bg-black/50" onClick={() => setMobileOpen(false)} />
        <div className="fixed left-0 top-0 z-50 h-full">{sidebarContent}</div>
      </>
    );
  }

  return sidebarContent;
}
